import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';
import { triggerRemoteFileDownload } from '@/app/shared/utils/file-download.util';
import { filesInPartOrder, ReportLinkFile } from '../models/report-link-download.model';
import { ReportLinkDownloadRepository } from '../services/report-link-download.repository';
import {
  REPORT_LINK_UNAVAILABLE,
  ReportLinkNotice,
  reportLinkDownloadNotice,
} from './report-link-download.notices';

@Injectable()
export class ReportLinkDownloadStore {
  private readonly repository = inject(ReportLinkDownloadRepository);
  private readonly toasts = inject(ToastService);
  private subscription: Subscription | null = null;
  private readonly _downloading = signal(false);

  readonly isDownloading = this._downloading.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.subscription?.unsubscribe());
  }

  download(requestId: string): void {
    if (this._downloading()) return;
    this._downloading.set(true);
    this.subscription = this.repository.download(requestId).subscribe({
      next: (files) => this.deliver(files),
      error: (cause: unknown) => {
        this.finish();
        this.notify(reportLinkDownloadNotice(cause));
      },
      complete: () => this.finish(),
    });
  }

  private deliver(files: readonly ReportLinkFile[]): void {
    const ordered = filesInPartOrder(files);
    if (ordered.length === 0 || ordered.some((file) => file.url.trim() === '')) {
      this.notify({ tone: 'error', message: REPORT_LINK_UNAVAILABLE });
      return;
    }
    for (const file of ordered) {
      triggerRemoteFileDownload(file.url, downloadName(file));
    }
  }

  private notify(notice: ReportLinkNotice | null): void {
    if (!notice) return;
    this.toasts[notice.tone](notice.message);
  }

  private finish(): void {
    this.subscription = null;
    this._downloading.set(false);
  }
}

function downloadName(file: ReportLinkFile): string {
  const name = file.fileName.trim();
  return name || `reporte-parte-${file.part}`;
}
